import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
// import { useFormSubmission, extractStudentInfo } from '../hooks/useFormSubmission';
import { 
  GraduationCap, 
  TrendingUp, 
  BarChart3, 
  PlusCircle, 
  Edit3, 
  Trash2, 
  Search, 
  Download,
  Bell,
  DollarSign,
  UserCheck,
  LogOut,
  Menu,
  X,
  FileSpreadsheet,
  Building,
  Clock,
  RefreshCw,
  CheckCircle,
  TrendingDown
} from 'lucide-react';

interface Admission {
  id: string;
  admissionNumber: string;
  studentName: string;
  college: string;
  community: 'MQ' | 'GQ';
  referralName: string;
  consultancyAmount: number;
  date: string;
  status: 'Pending' | 'Confirmed' | 'Completed';
  contactNumber: string;
  course: string;
  consultant: string;
  lastUpdated?: string;
  updatedBy?: string;
  updateHistory?: Array<{
    date: string;
    updatedBy: string;
    field: string;
    oldValue: string;
    newValue: string;
  }>;
}

interface DailyAnalytics {
  date: string;
  totalAdmissions: number;
  totalAmount: number;
  pendingAdmissions: number;
  completedAdmissions: number;
  avgConsultancyFee: number;
}

interface AdmissionFormProps {
  admission: Admission;
  onSave: (admission: Admission) => void;
  onCancel: () => void;
}

const AdmissionFormComponent: React.FC<AdmissionFormProps> = ({ admission, onSave, onCancel }) => {
  const [formData, setFormData] = useState<Admission>(admission);
  const isNewStudent = !admission.studentName; // Check if this is a new student

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'consultancyAmount' ? Number(value) : value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.studentName.trim()) {
      alert('Student name is required');
      return;
    }
    if (!formData.contactNumber.trim()) {
      alert('Contact number is required');
      return;
    }
    
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Admission Number
          </label>
          <input
            type="text"
            name="admissionNumber"
            value={formData.admissionNumber}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
            placeholder="Auto-generated admission number"
            title="Auto-generated admission number"
            readOnly
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date
          </label>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Student Name *
          </label>
          <input
            type="text"
            name="studentName"
            value={formData.studentName}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter student name"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Contact Number *
          </label>
          <input
            type="tel"
            name="contactNumber"
            value={formData.contactNumber}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="+91 9876543210"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Course
          </label>
          <input
            type="text"
            name="course"
            value={formData.course}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="MBBS, B.Tech, etc."
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Community
          </label>
          <select
            name="community"
            value={formData.community}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            title="Select community"
          >
            <option value="MQ">MQ</option>
            <option value="GQ">GQ</option>
          </select>
        </div>
        
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            College Name
          </label>
          <input
            type="text"
            name="college"
            value={formData.college}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="College name"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Referral Name
          </label>
          <input
            type="text"
            name="referralName"
            value={formData.referralName}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Referral person name"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Consultancy Amount
          </label>
          <input
            type="number"
            name="consultancyAmount"
            value={formData.consultancyAmount}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="25000"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            title="Select status"
          >
            <option value="Pending">Pending</option>
            <option value="Confirmed">Confirmed</option>
            <option value="Completed">Completed</option>
          </select>
        </div>
      </div>
      
      {formData.lastUpdated && !isNewStudent && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-900 mb-2">Edit History</h4>
          <p className="text-sm text-blue-700">
            <strong>Last Updated:</strong> {formData.lastUpdated} by {formData.updatedBy}
          </p>
          <p className="text-sm text-blue-600 mt-1">
            💡 You can modify student details, contact information, and status
          </p>
        </div>
      )}
      
      {isNewStudent && (
        <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
          <p className="text-sm text-green-700">
            <strong>Adding New Student:</strong> Fill in all required fields marked with *
          </p>
          <p className="text-sm text-green-600 mt-1">
            📋 Admission number will be auto-generated upon saving
          </p>
        </div>
      )}
      
      <div className="flex justify-end space-x-4 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {isNewStudent ? 'Add Student' : 'Update Details'}
        </button>
      </div>
    </form>
  );
};

const ConsultancyDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdmissionForm, setShowAdmissionForm] = useState(false);
  const [editingAdmission, setEditingAdmission] = useState<Admission | null>(null);
  const [notifications, setNotifications] = useState<string[]>([]);
  
  // Form submission hook removed

  // Get user info from localStorage
  const authData = JSON.parse(localStorage.getItem('consultancyAuth') || '{}');
  const currentUser = authData?.user?.email || 'Unknown User';
  const isConsultant = authData?.loginType === 'consultant';

  // Sample data
  const [admissions, setAdmissions] = useState<Admission[]>([
    {
      id: '1',
      admissionNumber: 'ADM-2025-001',
      studentName: 'Rajesh Kumar',
      college: 'Government Medical College, Chennai',
      community: 'MQ',
      referralName: 'Dr. Siva Karthi',
      consultancyAmount: 25000,
      date: '2025-08-13',
      status: 'Completed',
      contactNumber: '+91 9876543210',
      course: 'MBBS',
      consultant: 'siva.karthi@sewa.com',
      lastUpdated: '2025-08-13 14:30:00',
      updatedBy: 'siva.karthi@sewa.com'
    },
    {
      id: '2',
      admissionNumber: 'ADM-2025-002',
      studentName: 'Priya Sharma',
      college: 'Anna University, Chennai',
      community: 'GQ',
      referralName: 'Manikandan',
      consultancyAmount: 15000,
      date: '2025-08-13',
      status: 'Pending',
      contactNumber: '+91 9876543211',
      course: 'B.Tech CSE',
      consultant: 'manikandan@sewa.com',
      lastUpdated: '2025-08-13 10:15:00',
      updatedBy: 'manikandan@sewa.com'
    },
    {
      id: '3',
      admissionNumber: 'ADM-2025-003',
      studentName: 'Anandh Raj',
      college: 'SRM Institute of Science and Technology',
      community: 'MQ',
      referralName: 'Sathiyamoorthy',
      consultancyAmount: 20000,
      date: '2025-08-12',
      status: 'Confirmed',
      contactNumber: '+91 9876543212',
      course: 'BDS',
      consultant: 'sathiyamoorthy@sewa.com',
      lastUpdated: '2025-08-12 16:45:00',
      updatedBy: 'sathiyamoorthy@sewa.com'
    }
  ]);

  // Calculate analytics
  const calculateDailyAnalytics = (): DailyAnalytics[] => {
    const groupedByDate = admissions.reduce((acc, admission) => {
      const date = admission.date;
      if (!acc[date]) {
        acc[date] = {
          date,
          totalAdmissions: 0,
          totalAmount: 0,
          pendingAdmissions: 0,
          completedAdmissions: 0,
          avgConsultancyFee: 0
        };
      }
      
      acc[date].totalAdmissions++;
      acc[date].totalAmount += admission.consultancyAmount;
      
      if (admission.status === 'Pending') {
        acc[date].pendingAdmissions++;
      } else if (admission.status === 'Completed') {
        acc[date].completedAdmissions++;
      }
      
      return acc;
    }, {} as Record<string, DailyAnalytics>);

    // Calculate average consultancy fee
    Object.values(groupedByDate).forEach(analytics => {
      analytics.avgConsultancyFee = analytics.totalAmount / analytics.totalAdmissions;
    });

    return Object.values(groupedByDate).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  };

  const dailyAnalytics = calculateDailyAnalytics();
  const todayAnalytics = dailyAnalytics.find(a => a.date === new Date().toISOString().split('T')[0]) || {
    date: new Date().toISOString().split('T')[0],
    totalAdmissions: 0,
    totalAmount: 0,
    pendingAdmissions: 0,
    completedAdmissions: 0,
    avgConsultancyFee: 0
  };

  // Filter admissions based on user role and search term
  const filteredAdmissions = isConsultant 
    ? admissions.filter(admission => 
        admission.consultant === currentUser &&
        (admission.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
         admission.admissionNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
         admission.college.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : admissions.filter(admission =>
        admission.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        admission.admissionNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        admission.college.toLowerCase().includes(searchTerm.toLowerCase())
      );

  const handleLogout = () => {
    localStorage.removeItem('consultancyAuth');
    window.location.href = '/consultancy-login';
  };

  const handleAddAdmission = () => {
    setEditingAdmission(null);
    setShowAdmissionForm(true);
  };

  const handleAddStudent = () => {
    // Generate new admission number
    const newAdmissionNumber = `ADM-2025-${String(admissions.length + 1).padStart(3, '0')}`;
    
    const newAdmission: Admission = {
      id: Date.now().toString(),
      admissionNumber: newAdmissionNumber,
      studentName: '',
      college: '',
      community: 'MQ',
      referralName: currentUser.split('@')[0], // Use current user as referral
      consultancyAmount: 0,
      date: new Date().toISOString().split('T')[0],
      status: 'Pending',
      contactNumber: '',
      course: '',
      consultant: currentUser,
      lastUpdated: new Date().toISOString().replace('T', ' ').substring(0, 19),
      updatedBy: currentUser
    };
    
    setEditingAdmission(newAdmission);
    setShowAdmissionForm(true);
  };

  const handleEditAdmission = (admission: Admission) => {
    // Consultants can only edit their own admissions
    if (isConsultant && admission.consultant !== currentUser) {
      setNotifications(prev => [...prev, 'You can only edit your own admissions.']);
      return;
    }
    setEditingAdmission(admission);
    setShowAdmissionForm(true);
  };

  const handleUpdateAdmission = (updatedAdmission: Admission) => {
    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    
    // Check if this is a new admission (no existing ID in admissions array)
    const isNewAdmission = !admissions.find(admission => admission.id === updatedAdmission.id);
    
    if (isNewAdmission) {
      // Add new admission
      setAdmissions(prev => [...prev, {
        ...updatedAdmission,
        lastUpdated: now,
        updatedBy: currentUser
      }]);
      setNotifications(prev => [...prev, `New student ${updatedAdmission.studentName} added successfully.`]);
    } else {
      // Update existing admission
      setAdmissions(prev => prev.map(admission => 
        admission.id === updatedAdmission.id 
          ? {
              ...updatedAdmission,
              lastUpdated: now,
              updatedBy: currentUser
            }
          : admission
      ));
      setNotifications(prev => [...prev, `Admission ${updatedAdmission.admissionNumber} updated successfully`]);
    }
    
    // Store update in localStorage for admin dashboard
    const consultancyUpdates = JSON.parse(localStorage.getItem('consultancyUpdates') || '[]');
    consultancyUpdates.push({
      ...updatedAdmission,
      updatedAt: now,
      updatedBy: currentUser
    });
    localStorage.setItem('consultancyUpdates', JSON.stringify(consultancyUpdates));
    
    setShowAdmissionForm(false);
  };

  const handleDeleteAdmission = (admissionId: string) => {
    if (window.confirm('Are you sure you want to delete this admission?')) {
      setAdmissions(prev => prev.filter(admission => admission.id !== admissionId));
      setNotifications(prev => [...prev, 'Admission deleted successfully']);
    }
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredAdmissions);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Admissions');
    XLSX.writeFile(wb, `admissions_${new Date().toISOString().split('T')[0]}.xlsx`);
    setNotifications(prev => [...prev, 'Data exported to Excel successfully']);
  };

  const connectToHeadquarters = () => {
    setNotifications(prev => [...prev, 'Connected to SEWA Headquarters successfully']);
  };

  const syncData = () => {
    setNotifications(prev => [...prev, 'Data synchronized with headquarters']);
  };

  interface StatCardProps {
    icon: React.ReactNode;
    title: string;
    value: string | number;
    change?: number;
    color: string;
  }

  const StatCard = ({ icon, title, value, change, color }: StatCardProps) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-xl p-6 shadow-lg border-l-4 ${color}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm font-medium">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
          {change && (
            <p className={`text-sm mt-2 flex items-center ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change >= 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
              {Math.abs(change)}% from yesterday
            </p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color.replace('border-', 'bg-').replace('-500', '-100')}`}>
          {icon}
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <motion.div
        initial={{ x: -250 }}
        animate={{ x: sidebarOpen ? 0 : -250 }}
        className="w-64 bg-white shadow-lg fixed left-0 top-0 h-full z-30 overflow-y-auto"
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <img src="/SEWA.jpg" alt="SEWA" className="w-10 h-10 rounded-lg object-cover" />
            <div>
              <h2 className="font-bold text-lg text-gray-900">SEWA Consultancy</h2>
              <p className="text-sm text-gray-600">Dashboard</p>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-2">
          {[
            { id: 'overview', label: 'Overview', icon: <BarChart3 className="h-5 w-5" /> },
            { id: 'admissions', label: 'Admissions', icon: <GraduationCap className="h-5 w-5" /> },
            { id: 'analytics', label: 'Analytics', icon: <TrendingUp className="h-5 w-5" /> },
            { id: 'headquarters', label: 'Headquarters', icon: <Building className="h-5 w-5" /> },
            { id: 'reports', label: 'Reports', icon: <FileSpreadsheet className="h-5 w-5" /> },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.icon}
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="absolute bottom-4 left-4 right-4">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center space-x-3">
              <UserCheck className="h-8 w-8 text-blue-600" />
              <div>
                <p className="font-medium text-blue-900 text-sm">{currentUser}</p>
                <p className="text-blue-600 text-xs capitalize">{authData?.loginType || 'Consultant'}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full mt-3 flex items-center justify-center space-x-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-0'}`}>
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
              <h1 className="text-2xl font-bold text-gray-900">
                Consultancy Dashboard
              </h1>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={handleAddStudent}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <PlusCircle className="h-4 w-4" />
                <span>Add Student</span>
              </button>
              
              <button
                onClick={connectToHeadquarters}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Building className="h-4 w-4" />
                <span>Connect HQ</span>
              </button>
              
              <button
                onClick={syncData}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Sync</span>
              </button>

              <button
                onClick={exportToExcel}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="h-4 w-4" />
                <span>Export</span>
              </button>

              <div className="relative">
                <Bell className="h-6 w-6 text-gray-600" />
                {notifications.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {notifications.length}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Notifications */}
          <AnimatePresence>
            {notifications.map((notification, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -50 }}
                className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg"
              >
                <div className="flex items-center justify-between">
                  <p className="text-green-800">{notification}</p>
                  <button
                    onClick={() => setNotifications(prev => prev.filter((_, i) => i !== index))}
                    className="text-green-600 hover:text-green-800"
                    title="Dismiss notification"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  icon={<GraduationCap className="h-6 w-6 text-blue-600" />}
                  title="Today's Admissions"
                  value={todayAnalytics.totalAdmissions}
                  change={12}
                  color="border-blue-500"
                />
                <StatCard
                  icon={<DollarSign className="h-6 w-6 text-green-600" />}
                  title="Today's Revenue"
                  value={`₹${todayAnalytics.totalAmount.toLocaleString()}`}
                  change={8}
                  color="border-green-500"
                />
                <StatCard
                  icon={<Clock className="h-6 w-6 text-yellow-600" />}
                  title="Pending Admissions"
                  value={todayAnalytics.pendingAdmissions}
                  change={-5}
                  color="border-yellow-500"
                />
                <StatCard
                  icon={<CheckCircle className="h-6 w-6 text-purple-600" />}
                  title="Completed Today"
                  value={todayAnalytics.completedAdmissions}
                  change={15}
                  color="border-purple-500"
                />
              </div>

              {/* Recent Admissions Table */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Admissions</h3>
                
                {/* Consultant Capabilities Info */}
                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1">
                      <PlusCircle className="h-4 w-4 text-purple-600" />
                      <span className="text-sm font-medium text-blue-900">Add Student</span>
                    </div>
                    <span className="text-blue-600">•</span>
                    <div className="flex items-center space-x-1">
                      <Edit3 className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">Edit Details</span>
                    </div>
                    <span className="text-blue-600">•</span>
                    <div className="flex items-center space-x-1">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-blue-900">Update Status</span>
                    </div>
                  </div>
                </div>
                
                {/* Search and Actions */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                      type="text"
                      placeholder="Search admissions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleAddStudent}
                      className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      <PlusCircle className="h-4 w-4" />
                      <span>Add Student</span>
                    </button>
                    
                    {!isConsultant && (
                      <button
                        onClick={handleAddAdmission}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <PlusCircle className="h-4 w-4" />
                        <span>Add Admission</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Admission #</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Student</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">College</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Amount</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAdmissions.slice(0, 5).map((admission) => (
                        <tr key={admission.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm font-medium text-blue-600">
                            {admission.admissionNumber}
                          </td>
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-gray-900">{admission.studentName}</p>
                              <p className="text-sm text-gray-600">{admission.course}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {admission.college}
                          </td>
                          <td className="py-3 px-4 text-sm font-medium text-green-600">
                            ₹{admission.consultancyAmount.toLocaleString()}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              admission.status === 'Completed' ? 'bg-green-100 text-green-800' :
                              admission.status === 'Confirmed' ? 'bg-blue-100 text-blue-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {admission.status}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEditAdmission(admission)}
                                className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                                title="Edit admission"
                              >
                                <Edit3 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteAdmission(admission.id)}
                                className="p-1 text-red-600 hover:bg-red-100 rounded"
                                title="Delete admission"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Other tabs would go here - simplified for now */}
          {activeTab !== 'overview' && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Tab
              </h3>
              <p className="text-gray-600 mb-6">
                This tab will contain {activeTab} functionality. The basic structure is working!
              </p>
              
              {/* Show recent updates for admins */}
              {!isConsultant && activeTab === 'admissions' && (
                <div className="mt-8 bg-blue-50 rounded-lg p-6">
                  <h4 className="text-lg font-bold text-blue-900 mb-4">Recent Updates by Consultants</h4>
                  <div className="space-y-3">
                    {admissions
                      .filter(admission => admission.lastUpdated)
                      .sort((a, b) => new Date(b.lastUpdated!).getTime() - new Date(a.lastUpdated!).getTime())
                      .slice(0, 5)
                      .map(admission => (
                        <div key={admission.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div className="flex items-center space-x-3">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <div>
                              <p className="font-medium text-gray-900">{admission.admissionNumber}</p>
                              <p className="text-sm text-gray-600">{admission.studentName}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-blue-600">{admission.updatedBy}</p>
                            <p className="text-xs text-gray-500">{admission.lastUpdated}</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Admission Form Modal */}
      <AnimatePresence>
        {showAdmissionForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <h3 className="text-xl font-bold mb-4">
                {editingAdmission && editingAdmission.studentName ? 'Edit Student Details' : 'Add New Student'}
                {isConsultant && editingAdmission && editingAdmission.studentName && (
                  <span className="text-sm font-normal text-blue-600 ml-2">
                    (Consultant Mode)
                  </span>
                )}
              </h3>
              
              {editingAdmission ? (
                <AdmissionFormComponent 
                  admission={editingAdmission}
                  onSave={handleUpdateAdmission}
                  onCancel={() => setShowAdmissionForm(false)}
                />
              ) : (
                <div>
                  <p className="text-gray-600 mb-4">
                    Add new admission functionality would be implemented here for administrators.
                  </p>
                  <div className="flex justify-end space-x-4 mt-6">
                    <button
                      onClick={() => setShowAdmissionForm(false)}
                      className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setShowAdmissionForm(false)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Add Admission
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ConsultancyDashboard;